// frontend/src/services/builderService.ts
import axios from 'axios';
import { API_BASE_URL } from '@/config'; // Assuming you have a config file for your API base URL

export interface PageConfig {
  id: number;
  name: string;
  app_type: 'LOS' | 'LMS' | 'CRM' | 'ADMIN';
  components: string[];
  layout: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface JourneyConfig {
  id?: number;
  name: string;
  description?: string;
  app_type: 'LOS' | 'LMS' | 'CRM' | 'ADMIN';
  pages: any[]; // List of Page IDs or page objects
  flow_logic: {
    nodes: any[];
    edges: any[];
    sequence?: number[];
    branching?: Record<string, any>;
  };
  lender_id?: number;
  bap_id?: number;
  is_active?: boolean;
  is_default?: boolean;
  version?: number;
  // ── Source-based journey routing ──────────────────────────────────────────
  /** URL-safe slug used in ?source= query param. e.g. "phonepay_premium" */
  source_slug?: string | null;
  /** FRESH = create records on-the-fly; PRE_APPROVED = records already exist */
  entry_mode?: 'FRESH' | 'PRE_APPROVED';
  /** BAP name to use when entry_mode=FRESH */
  bap_name?: string | null;
}

export type NewPageConfig = Omit<PageConfig, 'id' | 'created_at' | 'updated_at'>;


const builderApi = axios.create({
  baseURL: `${API_BASE_URL}/api/builder`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  xsrfCookieName: 'apicsrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

export const getPageConfigs = async (): Promise<PageConfig[]> => {
  try {
    const response = await builderApi.get('/pages/');
    // Handle Django REST framework paginated responses where data is in .results
    if (response.data && Array.isArray(response.data.results)) {
      return response.data.results;
    }
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Failed to fetch page configs:", error);
    // In a real app, you'd handle this error more gracefully
    return [];
  }
};

export const getPageConfig = async (id: number): Promise<PageConfig> => {
    try {
        const response = await builderApi.get(`/pages/${id}/`);
        return response.data;
    } catch (error) {
        console.error(`Failed to fetch page config with id ${id}:`, error);
        throw error;
    }
};

export const getPageConfigBySlug = async (slug: string): Promise<PageConfig | null> => {
    try {
        // Use the dedicated by-slug endpoint which slugifies all page names server-side
        const response = await builderApi.get(`/pages/by-slug/${slug}/`);
        if (response.data && response.data.id) {
            return response.data as PageConfig;
        }
        return null;
    } catch (error: any) {
        // 404 means no page found for this slug - not a real error
        if (error?.response?.status === 404) {
            return null;
        }
        console.error(`Failed to fetch page config by slug "${slug}":`, error);
        return null;
    }
};

export const deletePageConfig = async (id: number): Promise<void> => {
  try {
    await builderApi.delete(`/pages/${id}/`);
  } catch (error) {
    console.error(`Failed to delete page config with id ${id}:`, error);
    throw error;
  }
};

export const savePageConfig = async (pageConfig: NewPageConfig): Promise<PageConfig> => {
  try {
    const response = await builderApi.post('/pages/', pageConfig);
    return response.data;
  } catch (error) {
    console.error("Failed to save page config:", error);
    // In a real app, you'd handle this error more gracefully
    throw error;
  }
};

// Journey / Flow API methods

export const getJourneyConfigs = async (): Promise<JourneyConfig[]> => {
  try {
    const response = await builderApi.get('/journeys/');
    if (response.data && Array.isArray(response.data.results)) {
      return response.data.results;
    }
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Failed to fetch journey configs:", error);
    return [];
  }
};

export const getJourneyConfig = async (id: number): Promise<JourneyConfig> => {
  try {
    const response = await builderApi.get(`/journeys/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch journey config with id ${id}:`, error);
    throw error;
  }
};

export const saveJourneyConfig = async (journey: JourneyConfig): Promise<JourneyConfig> => {
  try {
    if (journey.id) {
      const response = await builderApi.put(`/journeys/${journey.id}/`, journey);
      return response.data;
    } else {
      const response = await builderApi.post('/journeys/', journey);
      return response.data;
    }
  } catch (error) {
    console.error("Failed to save journey config:", error);
    throw error;
  }
};

export const deleteJourneyConfig = async (id: number): Promise<void> => {
  try {
    await builderApi.delete(`/journeys/${id}/`);
  } catch (error) {
    console.error(`Failed to delete journey config with id ${id}:`, error);
    throw error;
  }
};
